/**
 * Save-Progress Nudge — guest-only conversion banner.
 *
 * Triggers when an unauthenticated visitor has invested real effort:
 *   • Quote subtotal exceeds $1,500, OR
 *   • They've added 2+ custom items (project types or add-ons)
 *
 * Slides in once per session (we don't nag), persists dismissal via
 * sessionStorage so a refresh re-shows it but a no-thanks doesn't.
 * Logged-in users never see it.
 *
 * Why a slide-in (and not a modal): modals block flow and feel like a
 * paywall. A non-blocking sticky banner above the action buttons keeps
 * the user in flow but is impossible to miss.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';

const DISMISS_KEY = 'qs-save-nudge-dismissed';

const SaveProgressNudge = ({ user, quote, projectTypes, addOns }) => {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') setDismissed(true);
    } catch { /* sessionStorage disabled / private mode — show anyway */ }
  }, []);

  const customItems = useMemo(() => {
    const localPTs = (projectTypes || []).filter((p) => p._local).length;
    const localAOs = (addOns || []).filter((a) => a._local).length;
    return localPTs + localAOs;
  }, [projectTypes, addOns]);

  const subtotal = useMemo(() => {
    const raw = quote?.total ?? quote?.subtotal;
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  }, [quote]);

  // Triggers — must be a logged-out user with real effort invested.
  const shouldShow = !user && !dismissed && (subtotal >= 1500 || customItems >= 2);
  if (!shouldShow) return null;

  // Build a personalized message based on which trigger fired.
  let headline;
  if (subtotal >= 1500 && customItems >= 2) {
    headline = `You've built a $${Math.round(subtotal).toLocaleString()} quote with custom items`;
  } else if (subtotal >= 1500) {
    headline = `You've built a $${Math.round(subtotal).toLocaleString()} quote`;
  } else {
    headline = `You've added ${customItems} custom item${customItems > 1 ? 's' : ''}`;
  }

  const onDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
  };

  return (
    <div
      role="region"
      aria-label="Save your progress"
      data-testid="save-progress-nudge"
      className="relative rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-blue-50 p-4 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300"
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-700 rounded"
        data-testid="save-progress-nudge-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-emerald-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 text-sm leading-tight">
            {headline}
          </p>
          <p className="text-xs text-slate-600 mt-1">
            Sign up free in 10 seconds to save it, email it to your customer,
            and unlock the AI Photo Estimator.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Link
              to="/signup"
              className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md px-3 py-2"
              data-testid="save-progress-nudge-signup"
            >
              Sign up free → save this quote
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-md px-3 py-2"
              data-testid="save-progress-nudge-login"
            >
              Already have an account? Log in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaveProgressNudge;
